import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EnterHost } from "./EnterHost";
import { DnsInstructions } from "./DnsInstructions";
import { VerifyingStep } from "./VerifyingStep";
import { DnsVerifiedStep } from "./DnsVerifiedStep";
import { IssuingStep } from "./IssuingStep";
import { AssignTarget } from "./AssignTarget";
import { FailedStep } from "./FailedStep";

export interface DomainRow {
  id: string;
  hostname: string;
  displayHostname: string | null;
  kind: "apex" | "subdomain";
  wizardState:
    | "awaiting_dns"
    | "verifying"
    | "dns_verified"
    | "issuing"
    | "assigning"
    | "live"
    | "failed";
  target: { type: string | null; branch: string | null; deploymentId: string | null } | null;
  healthWarning: boolean;
  failedReason: string | null;
  verifyStartedAt: string | null;
  createdAt: string;
}

interface Props {
  orgSlug: string;
  projectSlug: string;
  /** id of an existing row to resume; null to start a fresh wizard from step 1. */
  rowId: string | null;
  onClose: () => void;
  /**
   * Called when the wizard needs to swap which row is open — used after
   * EnterHost creates a fresh row (swap `?domain=new` → `?domain=<id>`)
   * or when a same-project duplicate (409) returns an existingId.
   */
  onOpenId: (id: string) => void;
}

const POLL_STATES = new Set(["verifying", "issuing"]);

/**
 * WizardPanel — single in-progress row. The visible step is derived from
 * the row's wizard_state on every render, so refresh-resume is automatic.
 * For an active wizard (verifying/issuing), TanStack Query refetches the
 * row every 5 seconds so transitions surface promptly.
 *
 * The query intentionally reuses the page-wide ["project-domains", orgSlug,
 * projectSlug] key (rather than a per-row key like ["domain", ..., id]) so
 * a single refetch refreshes both the list AND the panel's row in one
 * round-trip. The polling refetchInterval is gated to the open panel's
 * own row state (only while verifying/issuing) — so polling for one row
 * doesn't run when a sibling row is alone in a transient state with the
 * panel closed. Multi-row independence is enforced at the DB layer (each
 * row's state machine guards on its own id + expected wizard_state), so
 * cache-level granularity is a perf concern not a correctness one.
 */
export function WizardPanel({ orgSlug, projectSlug, rowId, onClose, onOpenId }: Props) {
  const qc = useQueryClient();
  const list = useQuery({
    enabled: Boolean(rowId),
    queryKey: ["project-domains", orgSlug, projectSlug],
    queryFn: () =>
      api.get<{ domains: DomainRow[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/domains`),
    refetchInterval: (query) => {
      const data = query.state.data as { domains: DomainRow[] } | undefined;
      const row = data?.domains.find((d) => d.id === rowId);
      return row && POLL_STATES.has(row.wizardState) ? 5_000 : false;
    },
  });

  const row = rowId ? list.data?.domains.find((d) => d.id === rowId) ?? null : null;

  // Auto-close when the row goes live.
  useEffect(() => {
    if (row && row.wizardState === "live") {
      const t = setTimeout(() => onClose(), 1500);
      return () => clearTimeout(t);
    }
  }, [row, onClose]);

  return (
    <div className="of-card border-primary/40">
      <div className="of-card-header">
        <div className="text-sm font-semibold min-w-0 truncate">
          {row ? <>Wizard — <span className="font-mono">{row.hostname}</span></> : "Add custom domain"}
        </div>
        <Button size="icon" variant="ghost" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="px-4 py-4">
        {!rowId ? (
          <EnterHost
            orgSlug={orgSlug}
            projectSlug={projectSlug}
            onCreated={(id) => {
              qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] });
              onOpenId(id);
            }}
            onResumeExisting={(id) => onOpenId(id)}
          />
        ) : list.isLoading || !row ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : (
          <StepFor row={row} orgSlug={orgSlug} projectSlug={projectSlug} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function StepFor({
  row,
  orgSlug,
  projectSlug,
  onClose,
}: {
  row: DomainRow;
  orgSlug: string;
  projectSlug: string;
  onClose: () => void;
}) {
  switch (row.wizardState) {
    case "awaiting_dns":
      return (
        <DnsInstructions
          orgSlug={orgSlug}
          projectSlug={projectSlug}
          rowId={row.id}
          hostname={row.hostname}
          kind={row.kind}
        />
      );
    case "verifying":
      return (
        <VerifyingStep
          orgSlug={orgSlug}
          projectSlug={projectSlug}
          rowId={row.id}
          verifyStartedAt={row.verifyStartedAt}
        />
      );
    case "dns_verified":
      return <DnsVerifiedStep orgSlug={orgSlug} projectSlug={projectSlug} rowId={row.id} />;
    case "issuing":
      return <IssuingStep />;
    case "assigning":
      return (
        <AssignTarget
          orgSlug={orgSlug}
          projectSlug={projectSlug}
          rowId={row.id}
          submitLabel="Go live"
        />
      );
    case "live":
      return (
        <div className="text-xs space-y-2">
          <div className="text-success font-semibold">Live</div>
          <div className="text-muted-foreground">
            Serving traffic for <span className="font-mono">{row.hostname}</span>.
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      );
    case "failed":
      return (
        <FailedStep
          orgSlug={orgSlug}
          projectSlug={projectSlug}
          rowId={row.id}
          hostname={row.hostname}
          failedReason={row.failedReason}
          onDeleted={onClose}
        />
      );
  }
}
